import React, { useState, useEffect } from 'react';
import { Alert, Button, Modal, Typography, Row, Col, Statistic, Divider, Tag, Progress } from 'antd';
import { showMessage } from '@/utils/notificationHelper';
import { useSelector } from 'react-redux';
import { selectCurrentAdmin } from '@/redux/auth/selectors';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import {
    SafetyCertificateOutlined,
    TrophyOutlined,
    RiseOutlined,
    CheckCircleOutlined,
    GiftOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import confetti from 'canvas-confetti';
import logo from '@/style/images/logo.png';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const GlobalProfileAlert = () => {
    const currentAdmin = useSelector(selectCurrentAdmin);
    const [employeeDetails, setEmployeeDetails] = useState(null);
    const [isDismissed, setIsDismissed] = useState(sessionStorage.getItem('profileAlertDismissed') === 'true');
    const [showAppraisalModal, setShowAppraisalModal] = useState(false);
    const [showReminderModal, setShowReminderModal] = useState(false);
    const [hasBeenDismissed, setHasBeenDismissed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [nudgeNotification, setNudgeNotification] = useState(null);
    const history = useHistory();
    const location = useLocation();
    const [, setRerender] = useState(false);
    const [showSalary, setShowSalary] = useState(false);
    const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
    const [shouldShowAlert, setShouldShowAlert] = useState(false);

    const fetchEmployeeDetails = async () => {
        if (!currentAdmin?._id) return;
        try {
            const { data } = await axios.get('/my/profile');
            if (data.success) {
                setEmployeeDetails(data.employee);
            }
        } catch (err) {
            console.error('Failed to fetch employee details for alerts:', err);
        }
    };

    useEffect(() => {
        fetchEmployeeDetails();
    }, [currentAdmin?._id]);

    useEffect(() => {
        const handleRefresh = (e) => {
            const data = e?.detail;
            console.log('🔔 GlobalProfileAlert received event:', data);

            // Optimistically update reminder count
            const liveCount = data?.relatedData?.reminderCount;
            if (liveCount !== undefined) {
                setEmployeeDetails(prev => prev ? { ...prev, manualProfileReminderCount: liveCount } : null);
            }

            // CRITICAL: Immediate redirect on 3rd reminder (real-time blocking)
            if (liveCount >= 3 && location.pathname !== '/my-profile') {
                history.push('/my-profile');
            }

            // Universal Trigger: Pop up modal for EVERY reminder
            if (data?.notificationType === 'reminder') {
                setShowReminderModal(true);
                setHasBeenDismissed(false); // Reset dismissal on new hit
            }

            // HR Nudge Alert Trigger
            if (data?.notificationType === 'profile_nudge') {
                setNudgeNotification(data);
                setTimeout(() => setNudgeNotification(null), 10000); // Auto-hide after 10s
            }

            fetchEmployeeDetails();
        };

        window.addEventListener('profileStatusChanged', handleRefresh);
        window.addEventListener('refresh-profile-data', handleRefresh);
        window.addEventListener('reminder_received', handleRefresh);
        window.addEventListener('profile_nudge_received', handleRefresh);
        return () => {
            window.removeEventListener('profileStatusChanged', handleRefresh);
            window.removeEventListener('refresh-profile-data', handleRefresh);
            window.removeEventListener('reminder_received', handleRefresh);
            window.removeEventListener('profile_nudge_received', handleRefresh);
        };
    }, []);

    const handleAcknowledge = async () => {
        if (!employeeDetails?._id) return;
        setLoading(true);
        try {
            const response = await axios.post(`/employee/acknowledge/${employeeDetails._id}`);
            if (response.data.success) {
                showMessage.success('Acknowledgement recorded successfully!');
                setShowAppraisalModal(false);
                fetchEmployeeDetails(); // Refresh to hide banner

                // Final celebration
                confetti({
                    particleCount: 150,
                    spread: 100,
                    origin: { y: 0.6 }
                });
            }
        } catch (error) {
            console.error(error);
            showMessage.error('Failed to submit acknowledgement.');
        } finally {
            setLoading(false);
        }
    };

    // 1. Appraisal / Good News logic
    const state = employeeDetails?.acknowledgementState;
    const appraisalPending = state?.pending;
    const appraisalTypes = state?.types || (state?.type ? [state.type] : []);
    const details = state?.details;

    const renderUpdateCard = (type, index) => {
        const isLast = index === appraisalTypes.length - 1;

        switch (type) {
            case 'Confirmation':
                return (
                    <div key={type} style={{ marginBottom: isLast ? 0 : 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                            <SafetyCertificateOutlined style={{ fontSize: 32, color: '#52c41a' }} />
                            <div>
                                <Title level={5} style={{ margin: 0, color: '#52c41a' }}>Employment Confirmed</Title>
                                <Text type="secondary" style={{ fontSize: 13 }}>Status: Permanent</Text>
                            </div>
                        </div>
                        {details?.designation && (
                            <Text style={{ fontSize: 13, display: 'block', marginTop: 8 }}>
                                <strong>Designation:</strong> {details.designation}
                            </Text>
                        )}
                        {!isLast && <Divider style={{ margin: '16px 0' }} />}
                    </div>
                );

            case 'Increment':
                return (
                    <div key={type} style={{ marginBottom: isLast ? 0 : 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                            <RiseOutlined style={{ fontSize: 32, color: '#1890ff' }} />
                            <div style={{ flex: 1 }}>
                                <Title level={5} style={{ margin: 0, color: '#1890ff' }}>Salary Revised</Title>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
                                    <Text delete type="secondary" style={{ fontSize: 13 }}>
                                        PKR {details?.oldSalary?.toLocaleString()}
                                    </Text>
                                    <Text strong style={{ fontSize: 16, color: '#3f8600' }}>
                                        {showSalary ? `PKR ${details?.newSalary?.toLocaleString()}` : '••••••'}
                                    </Text>
                                    <Tag color="green" style={{ margin: 0 }}>+{((details?.newSalary - details?.oldSalary) / details?.oldSalary * 100).toFixed(1)}%</Tag>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={showSalary ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowSalary(!showSalary);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        {!isLast && <Divider style={{ margin: '16px 0' }} />}
                    </div>
                );

            case 'Promotion':
                return (
                    <div key={type} style={{ marginBottom: isLast ? 0 : 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                            <TrophyOutlined style={{ fontSize: 32, color: '#722ed1' }} />
                            <div>
                                <Title level={5} style={{ margin: 0, color: '#722ed1' }}>Promoted!</Title>
                                <div style={{ marginTop: 6 }}>
                                    <Text type="secondary" style={{ fontSize: 13 }}>{details?.oldDesignation}</Text>
                                    <Text style={{ margin: '0 8px' }}>→</Text>
                                    <Text strong style={{ fontSize: 14, color: '#722ed1' }}>{details?.newDesignation}</Text>
                                </div>
                            </div>
                        </div>
                        {!isLast && <Divider style={{ margin: '16px 0' }} />}
                    </div>
                );

            default:
                return null;
        }
    };

    const getModalTitle = () => {
        if (appraisalTypes.length > 1) {
            return '🎊 Multiple Career Updates!';
        }
        const type = appraisalTypes[0];
        if (type === 'Confirmation') return '🎊 Congratulations!';
        if (type === 'Increment') return '📈 Salary Update';
        if (type === 'Promotion') return '🏆 Promotion!';
        return '✨ Update';
    };

    const renderAppraisalModal = () => (
        <Modal
            title={null}
            open={showAppraisalModal}
            footer={null}
            onCancel={() => setShowAppraisalModal(false)}
            width={500}
            centered
            bodyStyle={{
                padding: '32px 28px',
                background: 'var(--modal-bg, #ffffff)',
                borderRadius: '12px'
            }}
            maskClosable={false}
            className="global-acknowledgement-modal"
        >
            <div style={{ textAlign: 'center' }}>
                {/* Logo - shown once at top */}
                <div style={{ marginBottom: 24 }}>
                    <img src={logo} alt="DymaxTech" style={{ height: '80px', objectFit: 'contain' }} />
                </div>

                {/* Title */}
                <Title level={3} style={{ marginBottom: 8, marginTop: 0 }}>
                    {getModalTitle()}
                </Title>

                <Paragraph type="secondary" style={{ marginBottom: 24, fontSize: 14 }}>
                    {appraisalTypes.length > 1
                        ? 'We are pleased to share multiple updates to your employment profile.'
                        : 'Please review and acknowledge the following update.'}
                </Paragraph>

                {/* Compact Update Cards */}
                <div style={{
                    textAlign: 'left',
                    background: 'var(--card-bg, rgba(0, 0, 0, 0.02))',
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: 24
                }}>
                    {appraisalTypes.map((type, index) => renderUpdateCard(type, index))}
                </div>

                {/* Action Button */}
                <Button
                    type="primary"
                    size="large"
                    block
                    icon={<CheckCircleOutlined />}
                    onClick={handleAcknowledge}
                    loading={loading}
                    style={{
                        height: 48,
                        fontSize: 16,
                        borderRadius: '24px',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                    }}
                >
                    I Acknowledge & Accept
                </Button>
            </div>

            <style>{`
                .global-acknowledgement-modal .ant-modal-content {
                    border-radius: 16px;
                    overflow: hidden;
                }
                
                /* Dark theme support */
                [data-theme='dark'] .global-acknowledgement-modal .ant-modal-content,
                .ant-theme-dark .global-acknowledgement-modal .ant-modal-content {
                    --modal-bg: #1f1f1f;
                    --card-bg: rgba(255, 255, 255, 0.05);
                }
                
                /* Light theme */
                [data-theme='light'] .global-acknowledgement-modal .ant-modal-content,
                .global-acknowledgement-modal .ant-modal-content {
                    --modal-bg: #ffffff;
                    --card-bg: rgba(0, 0, 0, 0.02);
                }
            `}</style>
        </Modal>
    );

    const renderAppraisalBanner = () => {
        if (!appraisalPending) return null;

        let bannerConfig = {
            message: "You have a new update! Click to view details.",
            icon: <GiftOutlined />,
            style: {
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                cursor: 'pointer',
                border: 'none',
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                fontWeight: 'bold'
            }
        };

        const isConfirmation = appraisalTypes.includes('Confirmation');
        const isIncrement = appraisalTypes.includes('Increment');
        const isPromotion = appraisalTypes.includes('Promotion');

        if (isConfirmation && isPromotion) {
            bannerConfig.message = "🎊 Amazing! You've been Confirmed AND Promoted! (Click to Acknowledge)";
            bannerConfig.icon = <TrophyOutlined />;
            bannerConfig.style.background = 'linear-gradient(90deg, #8E2DE2 0%, #4A00E0 100%)';
        } else if (isConfirmation) {
            bannerConfig.message = "🎊 Congratulations! Your employment status has been confirmed. (Click to Acknowledge)";
            bannerConfig.icon = <SafetyCertificateOutlined />;
            bannerConfig.style.background = 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)';
        } else if (isPromotion) {
            bannerConfig.message = "🏆 Outstanding Achievement! You have been promoted. (Click to Acknowledge)";
            bannerConfig.icon = <TrophyOutlined />;
            bannerConfig.style.background = 'linear-gradient(90deg, #8E2DE2 0%, #4A00E0 100%)';
        } else if (isIncrement) {
            bannerConfig.message = "📈 Great News! You have received a salary revision. (Click to Acknowledge)";
            bannerConfig.icon = <RiseOutlined />;
            bannerConfig.style.background = 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)';
        }

        return (
            <div onClick={() => {
                setShowAppraisalModal(true);
                confetti({ particleCount: 50, spread: 60, origin: { y: 0.8 } });
            }}>
                <Alert
                    message={<span style={{ color: 'white' }}>{bannerConfig.message}</span>}
                    type="info"
                    showIcon
                    icon={<span style={{ color: 'white' }}>{bannerConfig.icon}</span>}
                    style={bannerConfig.style}
                    className="global-appraisal-alert"
                />
            </div>
        );
    };

    // 2. Profile Verification / Incomplete logic
    const status = employeeDetails?.profileStatus || currentAdmin?.profileStatus;
    const empProfileType = employeeDetails?.user?.profileType || currentAdmin?.profileType || 'Full Employee';
    const isMinimalOrUserOnly = empProfileType === 'Minimal Profile' || empProfileType === 'User Only';
    const isIncomplete = !isMinimalOrUserOnly && (status === 'Incomplete' || !status);
    const isUnderReview = !isMinimalOrUserOnly && status === 'Under_HR_Review';
    const isUnverified = !isMinimalOrUserOnly && status === 'Unverified';
    const reminderCount = employeeDetails?.manualProfileReminderCount || 0;
    const completionPercentage = employeeDetails?.onboarding?.completionPercentage || 0;

    // Use the auto-trigger state for new reminders
    const isModalVisible = showReminderModal && location.pathname !== '/my-profile';
    const showAlertCondition = (isIncomplete || isUnderReview || isUnverified) && !isModalVisible;

    // Update shouldShowAlert state when conditions change
    useEffect(() => {
        setShouldShowAlert(showAlertCondition);
    }, [showAlertCondition]);

    const renderReminderModal = () => {
        if (!showAlertCondition || !isModalVisible) return null;

        const isCritical = reminderCount >= 3;
        return (
            <Modal
                title={null}
                open={true}
                footer={null}
                closable={isCritical ? false : true}
                onCancel={() => setShowReminderModal(false)}
                maskClosable={isCritical ? false : true}
                centered
                width={500}
                bodyStyle={{ padding: '40px 24px', textAlign: 'center' }}
            >
                {isCritical ? (
                    <CloseCircleOutlined style={{ fontSize: 64, color: '#f5222d', marginBottom: 20 }} />
                ) : (
                    <GiftOutlined style={{ fontSize: 64, color: '#faad14', marginBottom: 20 }} />
                )}

                <Title level={3} style={{ color: isCritical ? '#f5222d' : '#fa8c16', marginTop: 0 }}>
                    {isCritical ? 'CRITICAL: Profile Completion Overdue' : `Reminder #${reminderCount} from HR`}
                </Title>

                <Paragraph style={{ fontSize: 16 }}>
                    You have received <b>{reminderCount} reminders</b> to complete your profile.
                    {isCritical
                        ? " This is a final warning. Please finish your onboarding to avoid account restrictions."
                        : " Please upload all required documents to complete your professional profile."
                    }
                </Paragraph>

                <div style={{ margin: '30px 0', padding: '20px', background: isCritical ? '#fff1f0' : '#fffbe6', borderRadius: '8px' }}>
                    <Text strong>Current Progress: {completionPercentage}%</Text>
                    <Progress
                        percent={completionPercentage}
                        status={isCritical ? "exception" : "active"}
                        strokeColor={isCritical ? "#f5222d" : "#fa8c16"}
                        style={{ marginTop: 10 }}
                    />
                </div>

                <Button
                    type="primary"
                    size="large"
                    danger={isCritical}
                    block
                    onClick={() => {
                        history.push('/my-profile');
                        setShowReminderModal(false);
                        setHasBeenDismissed(true);
                    }}
                    style={{ height: 50, fontSize: 16, fontWeight: 'bold' }}
                >
                    {isCritical ? 'Go to My Profile & Complete Now' : 'Complete Profile Now'}
                </Button>
            </Modal>
        );
    };

    return (
        <>
            {renderAppraisalBanner()}
            {renderAppraisalModal()}
            {renderReminderModal()}

            {/* HR Nudge Modal */}
            <Modal
                title={null}
                open={!!nudgeNotification}
                footer={null}
                closable={true}
                onCancel={() => setNudgeNotification(null)}
                centered
                width={480}
                bodyStyle={{ padding: '40px 24px', textAlign: 'center' }}
            >
                <div style={{ marginBottom: 20 }}>
                    <span style={{ fontSize: 64 }}>👋</span>
                </div>
                <Title level={3} style={{ color: '#722ed1', marginTop: 0 }}>
                    Profile Review Request
                </Title>
                <Paragraph style={{ fontSize: 16 }}>
                    {nudgeNotification?.message || 'An employee is requesting you to review their profile.'}
                </Paragraph>
                <div style={{ margin: '30px 0', padding: '20px', background: '#f9f0ff', borderRadius: '8px', border: '1px solid #d3adf7' }}>
                    <Text strong>Please review and approve the employee's profile at your earliest convenience.</Text>
                </div>
                <Button
                    type="primary"
                    size="large"
                    block
                    onClick={() => {
                        history.push(`/employees/show/${nudgeNotification?.relatedData?.employeeId}`);
                        setNudgeNotification(null);
                    }}
                    style={{
                        height: 50,
                        fontSize: 16,
                        fontWeight: 'bold',
                        backgroundColor: '#722ed1',
                        borderColor: '#722ed1'
                    }}
                >
                    View Employee Profile
                </Button>
            </Modal>

            {shouldShowAlert && (
                <div style={{
                    position: 'fixed',
                    top: '80px',
                    left: 0,
                    right: 0,
                    zIndex: 999,
                    padding: '0 24px'
                }}>
                    {isUnderReview ? (
                        <Alert
                            message="Profile Under HR Review: Your profile is being reviewed by HR. You'll be notified once it's verified. Some sections may be locked during this period"
                            type="info"
                            showIcon
                            closable
                            onClose={() => setShouldShowAlert(false)}
                            className="global-profile-alert"
                        />
                    ) : isUnverified ? (
                        <Alert
                            message="Profile Unverified: Your profile needs verification. Please complete all required sections and upload documents for HR review."
                            type="warning"
                            showIcon
                            closable
                            onClose={() => setShouldShowAlert(false)}
                            className="global-profile-alert"
                            action={
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <Button
                                        size="small"
                                        type="primary"
                                        onClick={() => {
                                            history.push('/my-profile');
                                        }}
                                    >
                                        Complete Profile
                                    </Button>
                                </div>
                            }
                        />
                    ) : (
                        <Alert
                            message="Profile Incomplete - Action Required: Salary processing is on hold until you complete your profile and upload all required documents."
                            type="error"
                            showIcon
                            closable
                            onClose={() => setShouldShowAlert(false)}
                            className="global-profile-alert"
                            action={
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <Button
                                        size="small"
                                        type="primary"
                                        onClick={() => {
                                            history.push('/my-profile');
                                        }}
                                    >
                                        Complete Now
                                    </Button>
                                </div>
                            }
                        />
                    )}
                </div>
            )}
        </>
    );

    return null;
};

export default GlobalProfileAlert;
